/* eslint-disable no-param-reassign */
import Vue, { PluginObject, VueConstructor } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import i18n from '@/i18n';
import { capitalize } from '@/shared/helpers/capitalize';
import { encodeHtml } from '@/shared/helpers/encode';
import { decodeHtml } from '@/shared/helpers/decode';
import { staticSelectOptions } from '@/shared/helpers/static-select-options';
import { animateElement } from '@/shared/helpers/animate-element';
import { cloneObject } from '@/shared/helpers/clone-object';
import { formatUrl } from '@/shared/helpers/format-url';
import { snakeToCamel } from '@/shared/helpers/snake-to-camel';
import { snakeToCapitalized } from '@/shared/helpers/snake-to-capitalized';
import { subsetObject } from '@/shared/helpers/subset-object';
import { secondsToTime } from '@/shared/helpers/seconds-to-time';
import { isObjectDifferent } from '@/shared/helpers/is-object-different';
import { formatNumberToPhone } from '@/shared/helpers/format-number-to-phone';
import { formatToUrl } from '@/shared/helpers/format-to-url';
import { removeHtml } from '@/shared/helpers/remove-html';
import globalMixin from '@/mixins/global-mixin';
import rootMixin from '@/mixins/root-mixin';
import store from '@/store';
import {
  formatAutoResponderDate,
  formatDate,
  formatTime,
  formatDateTimeCustom,
} from '@/shared/helpers/date-helpers';

export const vueFilterFunctions = {
  rsDateTime: (value: string | Date) => (dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD hh:mm') : (value || '')),
  rsFullDateTime: (value: string | Date) => (dayjs(value).isValid() ? dayjs(value).format('MMMM D, YYYY, hh:mm A') : (value || '')),
  rsMedDateTime: (value: string | Date) => (dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY, hh:mm A') : (value || '')),
  rsDate: (value: string | Date) => (value ? dayjs(value).format('YYYY-MM-DD') : ''),
  rsFancyDate: (value: string | Date) => {
    if (value) {
      const date = dayjs(value).format('MMMM D, YYYY');
      return date;
    }

    return '';
  },
  rsFancyKey: (value: string) => (value ? capitalize(value.replace(/_|-/g, ' ')) : ''),
  rsISODateTime: (value: any) => (dayjs(value).isValid() ? dayjs(value).toISOString() : 'INVALID DATE'),
  rsUnixSecondsToDateTime: (value: any, format: string = '') => dayjs.unix(parseInt(value, 10)).format(format),
  rsFilename: (value: string) => (value ? value.split(/_(.+)/)[1] : ''),
  rsFancyFilename: (value: string) => (value ? value.split('/').pop() : ''),
  rsDecodeHtml: (value: string, doubleDecode: boolean = false) => (value ? decodeHtml(value, doubleDecode) : ''),
  capitalize: (value: string) => {
    if (!value) {
      return '';
    }

    const val = value.toString();
    return val.charAt(0).toUpperCase() + val.slice(1);
  },
};

export const globalUtils = {
  ...vueFilterFunctions,
  capitalize,
  decodeHtml,
  encodeHtml,
  staticSelectOptions,
  animateElement,
  cloneObject,
  formatUrl,
  snakeToCamel,
  snakeToCapitalized,
  isObjectDifferent,
  subsetObject,
  secondsToTime,
  formatNumberToPhone,
  formatToUrl,
  removeHtml,
  formatDate,
  formatTime,
  formatAutoResponderDate,
  formatDateTimeCustom,
};

export type TUtilsMethods = typeof globalUtils;

export const RsUtilitiesPlugin: PluginObject<null> = {
  install(vue: VueConstructor) {
    // Align dayjs locale with the resolved i18n locale so all date formatting
    // (filters, $utils.formatDateTimeCustom, etc.) respects the active language
    // without requiring per-call locale handling or new translation keys.
    dayjs.locale(i18n.locale);

    vue.prototype.$utils = globalUtils;
    vue.prototype.$env = window.APP_CONFIG || { VUE_APP_ENV: 'development' };
    vue.prototype.$eventBus = new Vue();

    // Typed wrapper around $t(): returns string instead of TranslateResult,
    // removing the need for `as string` casts or String() wrappers in templates.
    vue.prototype.$ts = function $ts(key: string, ...args: any[]): string {
      return this.$t(key, ...args) as string;
    };

    vue.prototype.$rsGlobals = new Vue({
      store,
      mixins: [rootMixin],
    });

    vue.mixin(globalMixin as any);

    vue.filter('rsDateTime', vueFilterFunctions.rsDateTime);
    vue.filter('rsFullDateTime', vueFilterFunctions.rsFullDateTime);
    vue.filter('rsDate', vueFilterFunctions.rsDate);
    vue.filter('rsFancyDate', vueFilterFunctions.rsFancyDate);
    vue.filter('rsFancyKey', vueFilterFunctions.rsFancyKey);
    vue.filter('rsISODateTime', vueFilterFunctions.rsISODateTime);
    vue.filter('rsUnixSecondsToDateTime', vueFilterFunctions.rsUnixSecondsToDateTime);
    vue.filter('rsFilename', vueFilterFunctions.rsFilename);
    vue.filter('rsFancyFilename', vueFilterFunctions.rsFancyFilename);
    vue.filter('rsDecodeHtml', vueFilterFunctions.rsDecodeHtml);
    vue.filter('capitalize', vueFilterFunctions.capitalize);

    vue.directive('drag-value', (el: any, binding: any, vnode: any) => {
      /* eslint-disable-next-line */
      el.dragValue = binding.value;
    });

    vue.directive('click-outside', {
      bind(el: any, binding: any, vnode: any) {
        /* eslint-disable-next-line */
        el.clickOutsideEvent = (event: any) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },

      unbind(el: any, binding: any, vnode: any) {
        /* eslint-disable-next-line */
        el.clickOutsideEvent = (event: any) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    });
    vue.directive('drag-class', (el: any, binding: any, vnode: any) => {
      /* eslint-disable-next-line */
      el.dragClass = binding.value;
    });
    vue.directive('permalink', (el) => {
      const { value } = el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0];

      // eslint-disable-next-line no-param-reassign
      (el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0]).value = formatToUrl(value || '');
    });
    vue.directive('permalink-with-underscore', (el) => {
      const { value } = el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0];

      // eslint-disable-next-line no-param-reassign
      (el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0]).value = formatToUrl(value || '', true);
    });
  },
};
